<template>
  <v-dialog
    v-model="localOpen"
    persistent
    max-width="520"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    transition="dialog-bottom-transition"
  >
    <v-card v-if="row" class="dialog-card">
      <!-- Header -->
      <div class="dialog-head">
        <div class="d-flex align-center">
          <div class="min-w-0">
            <div class="text-overline opacity-70">Select amount for</div>
            <div class="text-h6 font-weight-bold text-truncate">
              {{ row.name }}
            </div>
          </div>
          <v-spacer />
          <v-btn icon @click="emitClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Big remaining -->
        <div class="mt-3 d-flex align-end">
          <div>
            <div class="text-caption opacity-70">Remaining</div>
            <div class="text-h4 font-weight-black">
              {{ remainingAmount | currency }}
            </div>
          </div>
          <v-spacer />
          <v-chip v-if="isPaid" color="grey darken-2" text-color="white" small>
            <v-icon left small>mdi-lock</v-icon>
            Fully Reserved
          </v-chip>
        </div>

        <!-- Progress -->
        <v-progress-linear
          class="mt-3"
          :value="percent"
          height="10"
          rounded
          background-opacity="0.15"
          color="teal"
        />
        <div class="d-flex justify-space-between mt-1 text-caption opacity-70">
          <div>Raised: {{ (row.received || 0) | currency }}</div>
          <div>Goal: {{ (row.amount || 0) | currency }}</div>
        </div>
      </div>

      <v-divider />

      <!-- Body -->
      <v-card-text class="pa-4">
        <!-- Reserve full -->
        <v-btn
          block
          large
          class="rounded-lg"
          color="success"
          :disabled="isPaid"
          @click="$emit('paid-in-full')"
        >
          <v-icon left>mdi-calendar-check</v-icon>
          Reserve full day ({{ remainingAmount | currency }})
        </v-btn>

        <!-- Quick amounts -->
        <div class="mt-4" v-if="smartQuickAmounts.length">
          <div class="d-flex align-center mb-2">
            <div class="text-subtitle-2 font-weight-bold">Quick amounts</div>
            <v-spacer />
            <div class="text-caption opacity-70">Tap to fill</div>
          </div>

          <v-row dense>
            <v-col
              v-for="(amt, i) in smartQuickAmounts"
              :key="amt + '-' + i"
              cols="4"
              sm="3"
            >
              <v-btn
                block
                class="quick-btn"
                :outlined="Number(localAmount) !== Number(amt)"
                :depressed="Number(localAmount) === Number(amt)"
                color="teal"
                @click="setAmount(amt)"
              >
                {{ amt | currency }}
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- Custom amount -->
        <div class="mt-2">
          <div class="text-subtitle-2 font-weight-bold mb-2">Custom amount</div>

          <v-text-field
            v-model="localAmount"
            prefix="$"
            outlined
            dense
            type="number"
            class="amount-field"
            placeholder="Enter amount"
            :error="amountError"
            :error-messages="amountErrorMsg"
            @input="onInput"
          />
          <div class="text-caption opacity-70 mt-1">
            Tip: quick amounts are based on what’s remaining.
          </div>
        </div>
      </v-card-text>

      <!-- Sticky actions -->
      <div class="dialog-actions">
        <v-btn text class="mr-2" @click="emitClose">Cancel</v-btn>
        <v-spacer />
        <v-btn
          large
          color="success"
          class="px-6"
          :disabled="isPaid || !canSubmit"
          @click="submit"
        >
          <v-icon left>mdi-check</v-icon>
          Add
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "DaySponsorDialog",
  props: {
    value: { type: Boolean, default: false }, // v-model
    row: { type: Object, default: null },

    // keep this prop so you don't break other callers,
    // but we won't use it unless you want fallback behavior
    quickAmounts: { type: Array, default: () => [] },
  },
  data() {
    return {
      localOpen: false,
      localAmount: null,
      amountError: false,
      amountErrorMsg: "",
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.localOpen = v;
      },
    },
    localOpen(v) {
      this.$emit("input", v);
      if (!v) this.resetErrors();
      if (v && this.row) {
        this.localAmount =
          this.row.donationAmount != null ? this.row.donationAmount : 10;
      }
    },
    row: {
      immediate: true,
      handler(r) {
        if (!r) return;
        this.localAmount = r.donationAmount != null ? r.donationAmount : 10;
      },
    },
  },
  computed: {
    remainingAmount() {
      const amount = Number(this.row?.amount || 0);
      const received = Number(this.row?.received || 0);
      const rem = amount - received;
      return rem > 0 ? rem : 0;
    },
    percent() {
      const amount = Number(this.row?.amount || 0);
      const received = Number(this.row?.received || 0);
      if (!amount) return 0;
      const pct = Math.floor((received / amount) * 100);
      if (pct < 0) return 0;
      if (pct > 100) return 100;
      return pct;
    },
    isPaid() {
      return this.percent >= 100;
    },
    canSubmit() {
      const v = Number(this.localAmount || 0);
      return v > 0 && v <= this.remainingAmount;
    },

    smartQuickAmounts() {
      const rem = Number(this.remainingAmount || 0);
      if (rem <= 0) return [];

      const remainingFixed = Number(rem.toFixed(2));

      // base small buttons
      const base = [10, 20, 30, 40, 50, 100];

      // after 100, go by 50s (150, 200, 250...) but never over remaining
      const steps = [];
      for (let v = 150; v < rem; v += 50) steps.push(v);

      // build list
      let list = [...base, ...steps, remainingFixed];

      // keep only values <= remaining
      list = list.filter((x) => Number(x) > 0 && Number(x) <= rem);

      // dedupe + sort
      list = Array.from(
        new Set(list.map((x) => Number(x.toFixed ? x.toFixed(2) : x)))
      )
        .map(Number)
        .sort((a, b) => a - b);

      // cap to 8 buttons, but keep the important ones:
      // - keep first 5 (small)
      // - keep 150 and 200 if present
      // - keep remaining (last)
      const MAX = 8;
      if (list.length <= MAX) return list;

      const last = list[list.length - 1];

      const keep = new Set();

      // first 5 small ones
      for (const v of list.slice(0, 5)) keep.add(v);

      // keep 150/200 if they exist and fit
      if (list.includes(150)) keep.add(150);
      if (list.includes(200)) keep.add(200);

      // always keep remaining
      keep.add(last);

      // if still short of MAX, fill from the middle (closest to half remaining)
      const result = Array.from(keep).sort((a, b) => a - b);

      if (result.length < MAX) {
        const target = rem / 2;
        const candidates = list
          .filter((v) => !keep.has(v) && v !== last)
          .sort((a, b) => Math.abs(a - target) - Math.abs(b - target));

        for (const v of candidates) {
          if (result.length >= MAX) break;
          keep.add(v);
          result.push(v);
        }
      }

      return Array.from(new Set(result)).sort((a, b) => a - b);
    },
  },
  methods: {
    resetErrors() {
      this.amountError = false;
      this.amountErrorMsg = "";
    },
    emitClose() {
      this.$emit("close");
      this.localOpen = false;
    },
    setAmount(amt) {
      this.resetErrors();
      this.localAmount = Number(amt).toFixed(2);
      this.onInput();
    },
    onInput() {
      this.resetErrors();
      const v = Number(this.localAmount || 0);

      if (v <= 0) {
        this.amountError = true;
        this.amountErrorMsg = "Enter an amount greater than 0";
        return;
      }

      if (v > this.remainingAmount) {
        this.amountError = true;
        this.amountErrorMsg = "Amount can’t be more than remaining";
      }
    },
    submit() {
      this.onInput();
      if (this.amountError) return;

      this.$emit("submit", Number(this.localAmount).toFixed(2));
      this.localOpen = false;
    },
  },
};
</script>

<style scoped>
.dialog-card {
  border-radius: 18px;
  overflow: hidden;
}

.dialog-head {
  padding: 16px;
  background: linear-gradient(
    180deg,
    rgba(0, 128, 128, 0.12),
    rgba(0, 0, 0, 0)
  );
}

.opacity-70 {
  opacity: 0.7;
}

.min-w-0 {
  min-width: 0;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quick-btn {
  border-radius: 12px;
  font-weight: 700;
}

.amount-field :deep(.v-input__slot) {
  border-radius: 14px !important;
}

.dialog-actions {
  position: sticky;
  bottom: 0;
  background: #fff;
  padding: 12px 12px 14px;
  display: flex;
  align-items: center;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
</style>